import { useEffect, useMemo, useState } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { rollingAverage, sleepSchedule, type SleepSchedulePoint } from '@/db/queries'

interface Props {
  daysBack: number
}

export default function SleepDriftChart({ daysBack }: Props) {
  const [points, setPoints] = useState<SleepSchedulePoint[]>([])

  useEffect(() => {
    sleepSchedule(daysBack).then(setPoints)
  }, [daysBack])

  const smoothed = useMemo(() => rollingAverage(points, 30), [points])

  const merged = useMemo(
    () =>
      points.map((p, i) => ({
        day: p.day,
        bed: p.bedtimeMinSinceNoon,
        wake: p.wakeMinSinceNoon,
        bedAvg: smoothed[i]?.bedtime ?? null,
        wakeAvg: smoothed[i]?.wake ?? null,
      })),
    [points, smoothed],
  )

  // Compute a drift summary: rolling avg at start vs end of range.
  const summary = useMemo(() => {
    if (smoothed.length < 30) return null
    const firstAvg = smoothed.slice(0, 30)
    const lastAvg = smoothed.slice(-30)
    const avg = (xs: (number | null)[]) => {
      const vs = xs.filter((v): v is number => v != null)
      return vs.length ? vs.reduce((a, b) => a + b, 0) / vs.length : null
    }
    const bedStart = avg(firstAvg.map((p) => p.bedtime))
    const bedEnd = avg(lastAvg.map((p) => p.bedtime))
    const wakeStart = avg(firstAvg.map((p) => p.wake))
    const wakeEnd = avg(lastAvg.map((p) => p.wake))
    return {
      bedDriftMin: bedStart != null && bedEnd != null ? bedEnd - bedStart : null,
      wakeDriftMin: wakeStart != null && wakeEnd != null ? wakeEnd - wakeStart : null,
    }
  }, [smoothed])

  if (!points.length) {
    return <div className="py-6 text-sm text-stone-500">Not enough sleep data yet.</div>
  }

  return (
    <div className="space-y-4">
      {summary && (
        <div className="text-sm text-stone-600">
          {summary.bedDriftMin != null && (
            <span>
              Bedtime drift:{' '}
              <strong className="text-stone-900 tabular-nums">
                {formatDrift(summary.bedDriftMin)}
              </strong>
              {' · '}
            </span>
          )}
          {summary.wakeDriftMin != null && (
            <span>
              Wake drift:{' '}
              <strong className="text-stone-900 tabular-nums">
                {formatDrift(summary.wakeDriftMin)}
              </strong>
            </span>
          )}
        </div>
      )}
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={merged} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgb(231 229 228)" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="rgb(168 162 158)" />
            <YAxis
              yAxisId="bed"
              tick={{ fontSize: 11, fill: 'rgb(120 113 108)' }}
              stroke="rgb(168 162 158)"
              width={50}
              domain={[ymin, ymax]}
              tickFormatter={minToClock}
              ticks={[600, 660, 720, 780, 840]}
              label={{
                value: 'Bedtime',
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 10, fill: 'rgb(168 162 158)' },
              }}
            />
            <YAxis
              yAxisId="wake"
              orientation="right"
              tick={{ fontSize: 11, fill: 'rgb(120 113 108)' }}
              stroke="rgb(168 162 158)"
              width={50}
              domain={[wakeYmin, wakeYmax]}
              tickFormatter={minToClock}
              ticks={[1020, 1080, 1140, 1200, 1260]}
              label={{
                value: 'Wake',
                angle: 90,
                position: 'insideRight',
                style: { fontSize: 10, fill: 'rgb(168 162 158)' },
              }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v: number, name: string) =>
                typeof v === 'number' ? [minToClock(v), labelFor(name)] : [v, name]
              }
            />
            <Scatter yAxisId="bed" dataKey="bed" fill="#a855f7" fillOpacity={0.4} />
            <Scatter yAxisId="wake" dataKey="wake" fill="#6366f1" fillOpacity={0.4} />
            <Line
              yAxisId="bed"
              type="monotone"
              dataKey="bedAvg"
              stroke="#a855f7"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              yAxisId="wake"
              type="monotone"
              dataKey="wakeAvg"
              stroke="#6366f1"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 text-xs text-stone-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-purple-500" /> Bedtime
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-indigo-500" /> Wake
        </span>
        <span className="text-stone-400">— 30-day rolling average</span>
      </div>
    </div>
  )
}

const ymin = 540 // 9pm
const ymax = 900 // 3am
const wakeYmin = 960 // 4am
const wakeYmax = 1320 // 10am

function minToClock(mins: number): string {
  // mins is minutes since noon. Convert back to wall-clock string like "10:30p" / "6:15a".
  const totalMins = ((Math.round(mins) % (24 * 60)) + 24 * 60) % (24 * 60)
  const absoluteHour = Math.floor(((totalMins / 60) + 12) % 24)
  const minute = totalMins % 60
  const period = absoluteHour < 12 ? 'a' : 'p'
  let hr12 = absoluteHour % 12
  if (hr12 === 0) hr12 = 12
  return `${hr12}:${String(minute).padStart(2, '0')}${period}`
}

function labelFor(name: string): string {
  if (name === 'bed') return 'Bedtime'
  if (name === 'wake') return 'Wake'
  if (name === 'bedAvg') return 'Bed (30d avg)'
  if (name === 'wakeAvg') return 'Wake (30d avg)'
  return name
}

function formatDrift(min: number): string {
  const abs = Math.abs(min)
  const sign = min > 0 ? 'later' : 'earlier'
  if (abs < 1) return 'no change'
  if (abs < 60) return `${Math.round(abs)} min ${sign}`
  const h = Math.floor(abs / 60)
  const m = Math.round(abs % 60)
  return m === 0 ? `${h}h ${sign}` : `${h}h ${m}m ${sign}`
}

const tooltipStyle = {
  background: 'rgb(255 255 255)',
  border: '1px solid rgb(214 211 209)',
  borderRadius: 8,
  fontSize: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
}
